import type {
  BusinessTypeItem,
  EcosystemItem,
  LandingContent,
} from '@/features/admin/content/landingContent'
import { digitsOnly, waLink } from './waLink'

/**
 * TODA la lógica de los enlaces del pie (goal portal-correcciones-3).
 *
 * Regla del goal: **cada enlace que se ve lleva a algún sitio real**. Lo que no
 * existe no se pinta, y una columna que se queda sin entradas desaparece
 * completa (cabecera incluida). Antes el pie eran 26 strings sueltos con
 * `href="#"`.
 *
 * Las columnas «Productos» y «Soluciones» se DERIVAN del CMS (`ecosystem` y
 * `businessTypes`), que ya trae etiqueta y destino propio: así lo que marketing
 * edita en la landing aparece en el pie sin desplegar, y ningún enlace se
 * podre. Los destinos del CMS son contenido editable → se validan por esquema
 * antes de pintarlos (`classifyHref`).
 */

/** Cómo se pinta el enlace (define el elemento, no el estilo). */
export type FooterLinkKind =
  /** Ruta de la SPA → `<Link to>` (un `<a>` recargaría la app entera). */
  | 'route'
  /**
   * Ancla de una sección de la landing (`/#hardware`) → `<a href>`, igual que el
   * Header (`Header.tsx`).
   *
   * ⚠️ Verificado en navegador (goal portal-correcciones-3): con `<Link to>`
   * react-router cambia el hash y **NO baja a la sección** — el enlace parece
   * funcionar y no lleva a ninguna parte, justo lo que este goal retira. Con
   * `<a>` el navegador salta al fragmento sin recargar cuando ya estás en la
   * landing, y desde otra página pública carga la landing en la sección.
   */
  | 'anchor'
  /** http(s) fuera del portal → `<a target="_blank" rel="noopener noreferrer">`. */
  | 'external'
  /** `mailto:` → `<a>` sin `target` (no abre pestaña). */
  | 'mail'

export interface FooterLink {
  label: string
  href: string
  kind: FooterLinkKind
}

export interface FooterColumn {
  title: string
  links: FooterLink[]
}

export interface FooterLinksInput {
  /**
   * `true` solo cuando el contenido del CMS es REAL (backend o caché de la
   * última visita). Mientras carga —o si la carga falla— las vistas públicas no
   * renderizan los defaults (regla de `landingContent.ts`): sin contenido real
   * no se derivan las columnas del CMS ni sus enlaces.
   */
  cmsReady: boolean
  ecosystem: EcosystemItem[]
  businessTypes: BusinessTypeItem[]
  contact: LandingContent['contact']
  hidden: Record<string, boolean> | undefined
  /** `VITE_COMUNIDAD_URL`; sin ella no hay comunidad a la que enlazar. */
  comunidadUrl: string | undefined
  isLoggedIn: boolean
}

/** Tope por columna del CMS: más entradas descuadran el diseño del pie. */
export const MAX_LINKS_PER_COLUMN = 6

/**
 * Ancla de la sección «Ecosistema» de la landing (`Ecosystem.tsx`), respaldo de
 * un módulo del CMS sin enlace propio.
 *
 * 🚨 NO es `/#productos`: `Products.tsx` declara ese id pero **nadie lo
 * renderiza** (`LandingPage.tsx` monta `<Ecosystem />`) — es código muerto y
 * enlazarlo cambiaría un enlace muerto por otro.
 */
const ECOSYSTEM_ANCHOR = '/#ecosistema'
/** Ancla de «Tipos de negocio» (`BusinessTypes.tsx`, `id="tipos"`). */
const BUSINESS_TYPES_ANCHOR = '/#tipos'

/**
 * Valida el destino y decide cómo pintarlo. Devuelve `null` cuando no hay a
 * dónde ir: los `href` del CMS son entrada de usuario, así que se van por
 * **lista blanca** de esquemas (`javascript:`, `data:` y compañía no se
 * renderizan nunca).
 */
export function classifyHref(
  raw: string | undefined | null,
): { href: string; kind: FooterLinkKind } | null {
  const value = (raw ?? '').trim()
  if (!value) return null

  // `//host` es una URL protocol-relative disfrazada de ruta interna.
  if (value.startsWith('//')) return null
  if (value.startsWith('/')) {
    return { href: value, kind: value.includes('#') ? 'anchor' : 'route' }
  }
  // Ancla suelta del CMS ("#precios") → ancla de la landing.
  if (value.startsWith('#')) return { href: `/${value}`, kind: 'anchor' }

  try {
    const { protocol } = new URL(value)
    if (protocol === 'http:' || protocol === 'https:') return { href: value, kind: 'external' }
    if (protocol === 'mailto:') return { href: value, kind: 'mail' }
    return null
  } catch {
    // Ni ruta ni URL absoluta (p. ej. "registro"): no se adivina.
    return null
  }
}

/** Enlace con destino validado; `null` si el destino no sirve. */
function link(label: string, raw: string | undefined | null): FooterLink | null {
  const clean = label.trim()
  if (!clean) return null
  const target = classifyHref(raw)
  return target ? { label: clean, ...target } : null
}

/** Columna del CMS: etiqueta del item + su destino, con respaldo al ancla. */
function cmsColumn(
  title: string,
  items: { label: string; href: string }[],
  fallbackAnchor: string,
): FooterColumn | null {
  const links = items
    .map((item) => link(item.label, item.href) ?? link(item.label, fallbackAnchor))
    .filter((l): l is FooterLink => l !== null)
    .slice(0, MAX_LINKS_PER_COLUMN)
  return links.length ? { title, links } : null
}

/**
 * Columnas del pie ya filtradas: solo entran enlaces con destino real.
 * Función pura — se prueba sin montar el DOM.
 */
export function buildFooterColumns({
  cmsReady,
  ecosystem,
  businessTypes,
  contact,
  hidden,
  comunidadUrl,
  isLoggedIn,
}: FooterLinksInput): FooterColumn[] {
  const isHidden = (key: string) => !!hidden?.[key]
  /** Una sección del CMS solo se enlaza si hay contenido real y está visible. */
  const showsSection = (key: string) => cmsReady && !isHidden(key)

  const columns: (FooterColumn | null)[] = []

  columns.push(
    showsSection('ecosystem')
      ? cmsColumn(
          'Productos',
          ecosystem.map((item) => ({ label: item.label, href: item.buttonHref })),
          ECOSYSTEM_ANCHOR,
        )
      : null,
  )

  columns.push(
    showsSection('businessTypes')
      ? cmsColumn(
          'Soluciones',
          businessTypes.map((item) => ({ label: item.label, href: item.link })),
          BUSINESS_TYPES_ANCHOR,
        )
      : null,
  )

  // Empresa: anclas de secciones que existen + el histórico de "Lo último"
  // (ruta real, mejor destino que el ancla #novedades) + el WhatsApp del CMS.
  // Blog quedó fuera: no existe y no hay decisión de crearlo.
  const empresa = [
    showsSection('hardware') ? link('Hardware', '/#hardware') : null,
    showsSection('pricing') ? link('Precios', '/#precios') : null,
    link('Lo último', '/lo-ultimo'),
    // Aplicaciones para descargar: ruta real, y solo mientras la sección esté
    // visible en el CMS (con `hidden.downloads` la página avisa de que no está
    // disponible, así que tampoco se enlaza desde aquí).
    showsSection('downloads') ? link('Descargas', '/descargas') : null,
    showsSection('contact') && digitsOnly(contact?.whatsapp ?? '')
      ? link('Contacto', waLink(contact.whatsapp, contact.whatsappMessage))
      : null,
  ].filter((l): l is FooterLink => l !== null)
  columns.push(empresa.length ? { title: 'Empresa', links: empresa } : null)

  // Soporte: la comunidad (si hay a dónde) y la entrada a la sesión. Centro de
  // ayuda, Estado del sistema y Documentación quedaron fuera: no existen.
  const soporte = [
    comunidadUrl ? link('Comunidad', comunidadUrl) : null,
    isLoggedIn ? link('Mi Perfil', '/app') : link('Iniciar sesión', '/login'),
  ].filter((l): l is FooterLink => l !== null)
  columns.push(soporte.length ? { title: 'Soporte', links: soporte } : null)

  return columns.filter((c): c is FooterColumn => c !== null)
}

/**
 * Enlaces legales del pie. «Cookies» se retiró: no hay página de cookies y su
 * contenido vive dentro de la política de privacidad — apuntar a `/policy`
 * con esa etiqueta engañaría.
 */
export const LEGAL_LINKS: FooterLink[] = [
  { label: 'Privacidad', href: '/policy', kind: 'route' },
  { label: 'Términos', href: '/terminos', kind: 'route' },
]
